import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Modal, Select, Switch } from 'antd';
import { safeUrlRule } from '@shared/utils/safeUrl';
import {
  CONTENT_LINK_TYPES,
  MENU_LINK_TYPES,
  MENU_TARGETS,
  type MenuItemCreateInput,
  type MenuItemNode,
  type MenuItemUpdateInput,
  type MenuLinkType,
} from '../types';

interface FormValues {
  label: string;
  linkType: MenuLinkType;
  url?: string;
  contentSlug?: string;
  target: string;
  isActive: boolean;
}

interface MenuItemFormModalProps {
  open: boolean;
  editing: MenuItemNode | null;
  /** Yeni öğe eklenirken bağlam etiketi (kök / üst öğe adı). */
  parentLabel?: string;
  saving: boolean;
  onCancel: () => void;
  onCreate: (input: MenuItemCreateInput) => void;
  onUpdate: (itemId: string, input: MenuItemUpdateInput) => void;
}

/**
 * Menü öğesi formu. Link tipi `url` ise ham URL, içerik tipleri (article/page/
 * campaign) ise contentSlug alanı gösterilir. parent/order form dışında (create'te
 * çağıran, düzenlemede reorder ile) yönetilir.
 */
export function MenuItemFormModal({
  open,
  editing,
  parentLabel,
  saving,
  onCancel,
  onCreate,
  onUpdate,
}: MenuItemFormModalProps) {
  const { t } = useTranslation(['menus', 'common', 'validation']);
  const td = t as unknown as (key: string) => string;
  const [form] = Form.useForm<FormValues>();
  const isEdit = editing !== null;
  const linkType = Form.useWatch('linkType', form) ?? 'url';
  const isContentLink = CONTENT_LINK_TYPES.includes(linkType);

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      label: editing?.label ?? '',
      linkType: editing?.linkType ?? 'url',
      url: editing?.url ?? '',
      contentSlug: editing?.contentSlug ?? '',
      target: editing?.target ?? '_self',
      isActive: editing?.isActive ?? true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const onOk = () => {
    void (async () => {
      try {
        const v = await form.validateFields();
        const content = CONTENT_LINK_TYPES.includes(v.linkType);
        const url = content ? null : v.url?.trim() || null;
        const contentSlug = content ? v.contentSlug?.trim() || null : null;
        if (isEdit && editing) {
          onUpdate(editing.id, {
            label: v.label.trim(),
            linkType: v.linkType,
            url,
            contentSlug,
            target: v.target,
            isActive: v.isActive,
          });
        } else {
          onCreate({
            label: v.label.trim(),
            linkType: v.linkType,
            url,
            contentSlug,
            target: v.target,
            isActive: v.isActive,
          });
        }
      } catch {
        // inline validation surfaced by AntD
      }
    })();
  };

  const title = isEdit
    ? t('item.editTitle')
    : parentLabel
      ? t('item.createChildTitle', { parent: parentLabel })
      : t('item.createTitle');

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={onOk}
      okText={isEdit ? t('common:actions.save') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={saving}
      destroyOnClose
      maskClosable={!saving}
    >
      <Form<FormValues> form={form} layout="vertical">
        <Form.Item
          name="label"
          label={t('item.label')}
          rules={[{ required: true, message: t('item.labelRequired') }]}
        >
          <Input placeholder={t('item.labelPlaceholder')} maxLength={120} />
        </Form.Item>
        <Form.Item name="linkType" label={t('item.linkType')} rules={[{ required: true }]}>
          <Select
            options={MENU_LINK_TYPES.map((lt) => ({ value: lt, label: td(`linkType.${lt}`) }))}
          />
        </Form.Item>
        {isContentLink ? (
          <Form.Item
            name="contentSlug"
            label={t('item.contentSlug')}
            help={t('item.contentSlugHint')}
            rules={[{ required: true, message: t('item.contentSlugRequired') }]}
          >
            <Input placeholder={t('item.contentSlugPlaceholder')} maxLength={200} />
          </Form.Item>
        ) : (
          <Form.Item
            name="url"
            label={t('item.url')}
            rules={[{ required: true, message: t('item.urlRequired') }, safeUrlRule(t('validation:safeUrl'))]}
          >
            <Input placeholder={t('item.urlPlaceholder')} maxLength={500} />
          </Form.Item>
        )}
        <Form.Item name="target" label={t('item.target')}>
          <Select
            options={MENU_TARGETS.map((tg) => ({ value: tg, label: td(`target.${tg}`) }))}
          />
        </Form.Item>
        <Form.Item name="isActive" label={t('item.isActive')} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
